// Phase 41 Plan 41-07 Task 2 — SLA "Hour Package" hero card on
// ProjectDetailPage. Surface 4d-A per CONTEXT D-3.
//
// Closes PAY-02 (config visible: hours, currency), PAY-03 (overage toggle
// state visible), PAY-09 (consumption snapshot displayed), PAY-10 (alert
// threshold chips with triggered indicator).
//
// English-only labels per D-Copy (RESEARCH Pitfall 8). The component itself
// contains zero non-ASCII tokens — the canonical Czech-source-term →
// English-target-term map (Hour Package / Monthly fee / Rules) lives in
// CONTEXT.md so the per-file grep gate stays at zero matches here.
//
// SLA_BALICEK_PAYMENT_TYPE_ID flows in from the parent (ProjectDetailPage
// passes the constant from frontend/src/constants/typeIds.ts) per checker
// I-13: never inline the literal `2` so a backend ID change updates one
// frontend file.
//
// Visual contract uses the v7 ms-* tokens loaded by Plan 41-01 + Pill /
// Card primitives ported in Plan 41-02.
//
// **Pitfall 3 — Backend gap noted (Phase 41 milestone-end backlog):**
// The shipped CurrentMonthConsumptionDTO (10 fields) does NOT include
// `paushalAmount` (project monthly fee). Including it would require
// extending either backend/src/services/projectPackageService.ts
// `getCurrentMonthConsumption` to read project.balicekAmount, OR the
// frontend Project type/select to surface balicekAmount/balicekCurrency.
// Plan instructions explicitly defer such extensions to a /gsd-quick — so
// the card OMITS the Monthly fee row for v7.0 and PAY-02 surfacing of
// monthly fee is documented as a follow-up in 41-07-SUMMARY.md.

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../ui/Card';
import { Pill } from '../ui/Pill';
import { Button } from '../common/Button';
import { useProjectPackageStatus } from '../../hooks/useProjectPackageStatus';
import { useUpdateProject } from '../../hooks/useProjects';
import { useAuth } from '../../services/authContext';
import { BILLING_LOCK_COLORS } from '../common/billingLockColors';
import { LEGACY_CZ_NUMBER_LOCALE, LEGACY_EN_NUMBER_LOCALE, formatNumber, numberFormatter } from '../../utils/localeFormat';

// UAT 2026-05-13 F-D2 — first/last day of current month in ISO for the
// timesheet-report deep link query (`from` / `to` params expected by
// ProjectDetailedReportPage filtersFromUrl parser).
function currentMonthIsoRange(): { from: string; to: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const first = new Date(y, m, 1);
  const last = new Date(y, m + 1, 0);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { from: iso(first), to: iso(last) };
}

function progressTone(pct: number): { bar: string; text: string } {
  if (pct >= 100) return { bar: '#F43F5E', text: '#BE123C' };
  if (pct >= 90) return { bar: '#F97316', text: '#9A3412' };
  if (pct >= 70) return { bar: '#F59E0B', text: '#B45309' };
  return { bar: '#10B981', text: '#047857' };
}

function thresholdPill(t: { percentage: number; crossed: boolean }) {
  // UAT 2026-05-13 F-D3 — crossed thresholds were too subtle (amber-on-cream).
  // Bump triggered pills to a high-contrast red so PMs immediately see which
  // alert tier the project is over.
  return t.crossed
    ? { bg: '#FEE2E2', fg: '#991B1B', label: `${t.percentage}% • triggered`, ring: '#FCA5A5' }
    : { bg: '#F4F1EC', fg: '#5C5C68', label: `${t.percentage}%`, ring: undefined };
}

function fmtAmount(value: string | null, currency: string | null): string {
  if (!value) return '';
  const num = Number(value);
  if (!Number.isFinite(num)) return value;
  if (!currency) return formatNumber(num, { maximumFractionDigits: 0 }, LEGACY_EN_NUMBER_LOCALE);
  try {
    return numberFormatter({
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }, LEGACY_EN_NUMBER_LOCALE).format(num);
  } catch {
    // Unknown currency code — fall back to plain number + ISO code suffix.
    return `${formatNumber(num, { maximumFractionDigits: 0 }, LEGACY_EN_NUMBER_LOCALE)} ${currency}`;
  }
}

export interface BalicekHeroCardProps {
  projectId: number;
  /** From `Project.paymentTypeId`; null when the project is on the default template. */
  paymentTypeId: number | null;
  /** Constant from `frontend/src/constants/typeIds.ts` (per checker I-13). */
  slaBalicekPaymentTypeId: number;
  /** UAT 2026-05-13 F-D6 — initial balicekAmount for inline edit; consumption
      DTO omits this column (per file comment line ~21), so the parent passes
      it from `project.balicekAmount` if/when surfaced. Optional for backwards
      compatibility while the response schema is being widened. */
  initialBalicekAmount?: string | null;
  /** Phase 72 Plan 04 (CUR-02/CUR-06) — provisional CZK equivalent of the
      Hour Package monthly fee (from `project.balicekAmountCzk`), for the
      view-mode Monthly fee row. null for a CZK fee (no conversion) or when
      the amount is unset. */
  balicekAmountCzk?: number | null;
  /** Phase 72 Plan 04 (CUR-02/CUR-06) — true when `balicekAmountCzk` is a
      read-time provisional estimate (foreign currency, open period).
      Drives the shared Provisional/Locked pill. */
  balicekAmountProvisional?: boolean;
  /** UAT 2026-05-13 F-D6 — admin OR project PM: shows the Edit toggle and
      enables the save mutation. View-only otherwise. */
  canManage?: boolean;
}

export function BalicekHeroCard({
  projectId,
  paymentTypeId,
  slaBalicekPaymentTypeId,
  initialBalicekAmount = null,
  balicekAmountCzk = null,
  balicekAmountProvisional = false,
  canManage = false,
}: BalicekHeroCardProps) {
  const isBalicek = paymentTypeId === slaBalicekPaymentTypeId;
  const { isAdmin } = useAuth();
  const { data, isLoading, isError } = useProjectPackageStatus(projectId, isBalicek);
  // UAT 2026-05-13 F-D6 — hooks must be called unconditionally before any
  // early return; mutation lives at the component root, draft state mirrors
  // the fields the inline edit form exposes.
  const updateMutation = useUpdateProject();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    balicekHours: '',
    balicekAmount: '',
    balicekCurrency: 'EUR' as 'CZK' | 'EUR' | 'USD',
    overLimitAllowed: false,
    alertThresholdsCsv: '',
  });
  const [error, setError] = useState<string | null>(null);

  // Re-seed draft when the consumption snapshot refreshes AND we're not
  // currently editing (avoids clobbering the user's in-progress edit).
  useEffect(() => {
    if (editing) return;
    const pkg = data?.data;
    if (!pkg) return;
    setDraft({
      balicekHours: pkg.balicekHours != null ? String(pkg.balicekHours) : '',
      balicekAmount: initialBalicekAmount ?? '',
      balicekCurrency: ((pkg.currency as 'CZK' | 'EUR' | 'USD') ?? 'EUR'),
      overLimitAllowed: pkg.overLimitAllowed,
      alertThresholdsCsv: pkg.thresholds.map((t) => t.percentage).join(', '),
    });
  }, [data?.data, editing, initialBalicekAmount]);

  // UAT 2026-05-13 — handleSave hoisted above the early returns so the
  // empty-state branch (no consumption data yet) can reuse the same save
  // logic via the inline edit form. Order matters: const declarations don't
  // hoist, so this MUST come before any return that references it.
  const handleSave = async () => {
    setError(null);
    const hours = parseInt(draft.balicekHours, 10);
    if (!Number.isInteger(hours) || hours < 1) {
      setError('Hours must be a positive integer.');
      return;
    }
    const amount = draft.balicekAmount.trim();
    if (!/^[0-9]+(\.[0-9]{1,4})?$/.test(amount)) {
      setError('Amount must be a number with up to 4 decimal places.');
      return;
    }
    const thresholds = draft.alertThresholdsCsv
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => Number.isInteger(n) && n >= 1 && n <= 200)
      .map((percentage) => ({ percentage }));
    if (draft.overLimitAllowed && thresholds.length === 0) {
      setError('At least one alert threshold is required when over-limit is enabled.');
      return;
    }
    try {
      await updateMutation.mutateAsync({
        id: projectId,
        input: {
          balicekHours: hours,
          balicekAmount: amount,
          balicekCurrency: draft.balicekCurrency,
          overLimitAllowed: draft.overLimitAllowed,
          alertThresholds: thresholds,
        },
      });
      setEditing(false);
    } catch (e) {
      setError((e as Error).message || 'Could not save settings.');
    }
  };

  if (!isBalicek) return null;

  // UAT 2026-05-13 F-D-Issue-A — when the consumption endpoint returns an
  // error OR no data yet (freshly created project that hasn't accrued any
  // consumption snapshot), render an empty-state card with an Edit button
  // for admin/PM instead of returning null (which made the second grid
  // column silently vanish — see Petra's Test 321 screenshot 2026-05-13).
  if (isError || (!isLoading && !data?.data)) {
    return (
      <Card
        title="Hour Package"
        action={
          canManage ? (
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
              Edit
            </Button>
          ) : undefined
        }
      >
        {editing ? null : (
          <p className="text-[12px] text-ms-text-secondary italic">
            No consumption data yet. Once time entries land on this project the
            current-month progress and threshold chips appear here.
          </p>
        )}
        {/* When editing from empty state, fall through to the edit form
            rendered later in this component. We can't reach it via the normal
            render flow because `pkg` is undefined here; render the form
            inline as a stripped-down variant. */}
        {editing && canManage && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] font-medium text-blue-900 mb-0.5">
                  Hours / month <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={draft.balicekHours}
                  onChange={(e) => setDraft((d) => ({ ...d, balicekHours: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-blue-900 mb-0.5">
                  Amount / month <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-1">
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="^[0-9]+(\.[0-9]{1,4})?$"
                    value={draft.balicekAmount}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, balicekAmount: e.target.value }))
                    }
                    className="flex-1 rounded-lg border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g. 50000"
                    required
                  />
                  <select
                    value={draft.balicekCurrency}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        balicekCurrency: e.target.value as 'CZK' | 'EUR' | 'USD',
                      }))
                    }
                    className="w-20 rounded-lg border border-gray-300 px-1 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="CZK">CZK</option>
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-blue-900 mb-0.5">
                Alert thresholds (%)
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={draft.alertThresholdsCsv}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, alertThresholdsCsv: e.target.value }))
                }
                className="w-full rounded-lg border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="80, 100, 120"
              />
            </div>
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="balicek-empty-overlimit"
                checked={draft.overLimitAllowed}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, overLimitAllowed: e.target.checked }))
                }
                className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="balicek-empty-overlimit" className="text-[12px] text-gray-800">
                Allow over-limit hours
              </label>
            </div>
            {error && (
              <div className="rounded-md border border-red-300 bg-red-50 px-2 py-1">
                <p className="text-[11px] text-red-800">{error}</p>
              </div>
            )}
            <div className="flex items-center gap-2 pt-1">
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                isLoading={updateMutation.isPending}
              >
                Save
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setEditing(false);
                  setError(null);
                }}
                type="button"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Card>
    );
  }

  if (isLoading || !data?.data) {
    return (
      <Card title="Hour Package">
        <div className="h-24 animate-pulse bg-ms-surface-2 rounded" />
      </Card>
    );
  }

  const pkg = data.data;
  const pctNum = Number(pkg.percentageConsumed);
  const pct = Number.isFinite(pctNum) ? pctNum : 0;
  const tone = progressTone(pct);
  const overageHoursNum = pkg.overageHours != null ? Number(pkg.overageHours) : 0;
  const showOverage = pkg.overLimitAllowed && overageHoursNum > 0;

  return (
    <Card
      title="Hour Package"
      action={
        <div className="flex items-center gap-2">
          {pkg.currency && !editing && (
            <Pill bg="#F4F1EC" fg="#5C5C68">
              {pkg.currency}
            </Pill>
          )}
          {canManage && !editing && (
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
              Edit
            </Button>
          )}
        </div>
      }
    >
      {editing ? (
        // UAT 2026-05-13 F-D6 — inline edit mode. Matches Petra's screenshot
        // mock: Hours/month + Amount/month at the top, Alert thresholds CSV
        // below, then Allow over-limit toggle. Currency dropdown sits next
        // to the amount input (was a small pill in view mode). Overage rates
        // editing is NOT in this form yet — defer to a follow-up since the
        // per-role array UI is substantial and the admin > SLA Tiers page
        // already covers per-project tier CRUD.
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[11px] font-medium text-blue-900 mb-0.5">
                Hours / month <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min={1}
                step={1}
                value={draft.balicekHours}
                onChange={(e) => setDraft((d) => ({ ...d, balicekHours: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-blue-900 mb-0.5">
                Amount / month <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-1">
                <input
                  type="text"
                  inputMode="decimal"
                  pattern="^[0-9]+(\.[0-9]{1,4})?$"
                  value={draft.balicekAmount}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, balicekAmount: e.target.value }))
                  }
                  className="flex-1 rounded-lg border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g. 50000"
                  required
                />
                <select
                  value={draft.balicekCurrency}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      balicekCurrency: e.target.value as 'CZK' | 'EUR' | 'USD',
                    }))
                  }
                  className="w-20 rounded-lg border border-gray-300 px-1 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="CZK">CZK</option>
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-medium text-blue-900 mb-0.5">
              Alert thresholds (%){draft.overLimitAllowed && <span className="text-red-500"> *</span>}
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={draft.alertThresholdsCsv}
              onChange={(e) =>
                setDraft((d) => ({ ...d, alertThresholdsCsv: e.target.value }))
              }
              className="w-full rounded-lg border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="80, 100, 120"
            />
            <p className="text-[11px] text-ms-text-secondary mt-0.5">
              Comma-separated percentages (1–200).{' '}
              {draft.overLimitAllowed
                ? 'Required when over-limit is enabled.'
                : 'Leave blank for no alerts.'}
            </p>
          </div>

          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="balicek-edit-overlimit"
              checked={draft.overLimitAllowed}
              onChange={(e) =>
                setDraft((d) => ({ ...d, overLimitAllowed: e.target.checked }))
              }
              className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="balicek-edit-overlimit" className="text-[12px] text-gray-800">
              Allow over-limit hours (overage rates managed in admin → SLA Tiers)
            </label>
          </div>

          {error && (
            <div className="rounded-md border border-red-300 bg-red-50 px-2 py-1">
              <p className="text-[11px] text-red-800">{error}</p>
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              isLoading={updateMutation.isPending}
            >
              Save
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditing(false);
                setError(null);
              }}
              type="button"
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <>
        <div className="flex items-baseline justify-between mb-1">
          <div>
            <span
              className="text-[28px] font-bold tabular-nums"
              style={{ color: tone.text }}
            >
              {pkg.consumedHours}
            </span>
            <span className="text-[14px] text-ms-text-muted tabular-nums">
              {' / '}
              {pkg.balicekHours ?? '—'}
              {' h'}
            </span>
          </div>
          <span className="text-[12.5px] font-semibold text-ms-text-secondary">
            {Math.round(pct)}% consumed
          </span>
        </div>

      {/* UAT 2026-05-13 F-D2 — deep link to the per-project detailed time
          report with the current month pre-selected so PMs can drill from
          "consumed X h" into the rows that produced it without re-filtering.
          Phase 48 TS-EDIT-07 — admins are routed to the admin timesheets
          queue instead so they can edit roleId on orphan entries inline. */}
      {(() => {
        const range = currentMonthIsoRange();
        const reportParams = new URLSearchParams({
          projectIds: String(projectId),
          from: range.from,
          to: range.to,
        });
        const adminParams = new URLSearchParams({
          state: 'all',
          projectId: String(projectId),
          from: range.from,
          to: range.to,
        });
        const href = isAdmin
          ? `/admin/timesheets/pending?${adminParams.toString()}`
          : `/reports/project/detailed?${reportParams.toString()}`;
        return (
          <Link
            to={href}
            className="inline-flex items-center text-[12px] text-blue-600 hover:text-blue-800 hover:underline mb-3"
          >
            View consumed hours in timesheet →
          </Link>
        );
      })()}

      <div className="h-2 bg-ms-surface-2 rounded-full overflow-hidden mb-4">
        <div
          className="h-full rounded-full"
          style={{ width: `${Math.min(100, pct)}%`, background: tone.bar }}
        />
      </div>

      <dl className="space-y-1.5 mb-4">
        {/* Phase 72 Plan 04 (CUR-02/CUR-06) — Monthly fee (the Hour Package
            monthly fee). Shown in view mode; for a foreign currency it appends the
            provisional CZK equivalent + the shared Provisional/Locked pill.
            A CZK fee shows just the amount, no pill. Amount comes from
            initialBalicekAmount (project.balicekAmount); currency from the
            consumption DTO (pkg.currency, sourced from balicekCurrency). */}
        {initialBalicekAmount != null && initialBalicekAmount !== '' && (() => {
          const feeNum = Number(initialBalicekAmount);
          const feeDisplay = Number.isFinite(feeNum)
            ? `${formatNumber(feeNum, { maximumFractionDigits: 0 }, LEGACY_CZ_NUMBER_LOCALE)} ${pkg.currency ?? 'CZK'}`
            : `${initialBalicekAmount} ${pkg.currency ?? 'CZK'}`;
          const isForeignFee = !!pkg.currency && pkg.currency !== 'CZK';
          return (
            <div className="flex justify-between items-center text-[12px] gap-2">
              <dt className="text-ms-text-secondary">Monthly fee</dt>
              <dd className="font-semibold tabular-nums flex items-center gap-1.5 flex-wrap justify-end">
                <span>{feeDisplay}</span>
                {isForeignFee && balicekAmountCzk != null && (
                  <span className="text-ms-text-muted font-normal">
                    (≈ {formatNumber(balicekAmountCzk, { maximumFractionDigits: 0 }, LEGACY_CZ_NUMBER_LOCALE)} CZK)
                  </span>
                )}
                {isForeignFee && (
                  balicekAmountProvisional ? (
                    <Pill
                      bg={BILLING_LOCK_COLORS.provisional.bg}
                      fg={BILLING_LOCK_COLORS.provisional.fg}
                      dot={BILLING_LOCK_COLORS.provisional.dot}
                    >
                      Provisional (open period)
                    </Pill>
                  ) : (
                    <Pill
                      bg={BILLING_LOCK_COLORS.locked.bg}
                      fg={BILLING_LOCK_COLORS.locked.fg}
                      dot={BILLING_LOCK_COLORS.locked.dot}
                    >
                      Locked
                    </Pill>
                  )
                )}
              </dd>
            </div>
          );
        })()}
        <div className="flex justify-between text-[12px]">
          <dt className="text-ms-text-secondary">Overage allowed</dt>
          <dd className="font-semibold">{pkg.overLimitAllowed ? 'On' : 'Off'}</dd>
        </div>
        {pkg.carryoverHours != null && pkg.carryoverHours > 0 && (
          <div className="flex justify-between text-[12px]">
            <dt className="text-ms-text-secondary">Carryover applied</dt>
            <dd className="font-semibold tabular-nums text-emerald-700">
              +{pkg.carryoverHours.toFixed(1)} h
            </dd>
          </div>
        )}
        {showOverage && (
          <div className="flex justify-between text-[12px]">
            <dt className="text-ms-text-secondary">Overage this month</dt>
            <dd className="font-semibold tabular-nums text-amber-700">
              {overageHoursNum.toFixed(2)} h
              {pkg.overageAmount != null && ` • ${fmtAmount(pkg.overageAmount, pkg.currency)}`}
            </dd>
          </div>
        )}
      </dl>

      {/* UAT 2026-05-13 F-D3 — render the section even when no thresholds
          exist, with an empty-state hint linking to the edit form. Hides the
          common "I crossed 80% but see nothing" confusion when the project
          was created without alert thresholds. */}
      <div>
        <div className="text-[10.5px] font-semibold uppercase tracking-wider text-ms-text-muted mb-1.5">
          Alert thresholds
        </div>
        {pkg.thresholds.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {pkg.thresholds.map((t) => {
              const cfg = thresholdPill(t);
              return (
                <Pill key={t.percentage} bg={cfg.bg} fg={cfg.fg}>
                  {cfg.label}
                </Pill>
              );
            })}
          </div>
        ) : (
          <p className="text-[12px] text-ms-text-secondary italic">
            None configured.{' '}
            {canManage ? (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-blue-600 hover:text-blue-800 hover:underline not-italic"
              >
                Add thresholds
              </button>
            ) : (
              <Link
                to={`/projects/${projectId}/edit`}
                className="text-blue-600 hover:text-blue-800 hover:underline not-italic"
              >
                Add thresholds in project edit
              </Link>
            )}
            .
          </p>
        )}
      </div>
      </>
      )}
    </Card>
  );
}
